import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconEdit, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadListCsv } from '../utils/csv';
import { formatDateTime } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/Announcements.jsx — ported as-is
// (generic module, not renamed). Not permission-gated: `/announcements` is
// open to any authenticated user (see routes/api.php).
interface AnnouncementRow {
  id: number;
  title: string;
  body: string;
  priority: 'normal' | 'important' | 'urgent';
  published_at: string | null;
  expires_at: string | null;
  author: { full_name: string } | null;
}

const NO_FILTERS = {};

export function Announcements() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const { data, loading, error, setPage, reload: load } = usePaginatedList<AnnouncementRow>(
    '/announcements',
    NO_FILTERS
  );

  const remove = async (a: AnnouncementRow) => {
    if (!(await confirmDelete(`Delete "${a.title}"?`))) return;
    try {
      await api.delete(`/announcements/${a.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<AnnouncementRow>(
      api,
      '/announcements',
      {},
      [
        { label: 'Title', value: (r) => r.title },
        { label: 'Body', value: (r) => r.body },
        { label: 'Priority', value: (r) => r.priority },
        { label: 'Published', value: (r) => r.published_at },
        { label: 'Expires', value: (r) => r.expires_at },
        { label: 'Author', value: (r) => r.author?.full_name },
      ],
      'announcements.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Announcements</h1>
        <div className="page-header-actions">
          <IconButton
            icon={IconDownload}
            label={exporting ? 'Exporting…' : 'Download CSV'}
            onClick={exportCsv}
            disabled={exporting}
          />
          <Link className="btn btn-primary" to="/announcements/new">
            + New announcement
          </Link>
        </div>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Announcement</th>
                <th>Priority</th>
                <th>Published</th>
                <th>Expires</th>
                <th>By</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <strong>{a.title}</strong>
                      <div>
                        <small className="muted">{a.body}</small>
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${a.priority}`}>{a.priority}</span>
                    </td>
                    <td>{formatDateTime(a.published_at)}</td>
                    <td>{formatDateTime(a.expires_at)}</td>
                    <td>{a.author?.full_name ?? '—'}</td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/announcements/${a.id}`} />
                      <IconButton icon={IconEdit} label="Edit" to={`/announcements/${a.id}/edit`} />
                      <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(a)} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No announcements yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
